/**
 * Shared audience-attribute helpers for Quick Study — used by both the insight-card
 * segment selector (QuickStudyPage) and the composition side pane.
 *
 * "Clean" demographic-style keys only: skip internal `_`-prefixed fields, long
 * free-text columns, and survey-question keys (numbered / containing '?').
 */

import { AudienceTag } from '@digital-twins/shared-types'

import {
  CATEGORY_ORDER,
  getCategoryForCanonicalId,
  type SegmentationCategory,
} from '@/types/components/audiences/segmentation-mapping'

export function isCleanSegmentKey(key: string): boolean {
  if (!key || key.startsWith('_')) return false
  if (key.length > 28) return false
  if (/[?\d]/.test(key)) return false
  return true
}

export function humanizeAttr(name: string): string {
  const s = name.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : name
}

/**
 * QS-25 — known French demographic VALUE → concise English. Audiences built from
 * French source data surface raw French attribute values (income tiers, education
 * levels, gender) even on an English study. This is a small, curated lookup for the
 * common cases only — NOT a general translator; unknown values fall back to
 * `humanizeValue` (light tidy) so we never mangle a value we don't recognize.
 *
 * Keys are normalized (lowercased, accent-stripped, whitespace-collapsed) so a match
 * is tolerant of casing/accents ("Niveau Master" === "niveau master").
 */
const FRENCH_VALUE_MAP: Record<string, string> = {
  // Gender
  homme: 'Male',
  femme: 'Female',
  masculin: 'Male',
  feminin: 'Female',
  autre: 'Other',
  // Education levels (CITE / French ISCED-style labels)
  'enseignement primaire': 'Primary education',
  'premier cycle de l enseignement secondaire': 'Lower secondary',
  'deuxieme cycle de l enseignement secondaire': 'Upper secondary',
  'enseignement secondaire': 'Secondary education',
  'enseignement superieur': 'Higher education',
  'enseignement superieur de cycle court': 'Short-cycle tertiary',
  'enseignement superieur de cycle long': 'Long-cycle tertiary',
  'enseignement superieur universitaire': 'University education',
  'niveau licence ou equivalent': "Bachelor's or equivalent",
  'niveau master ou equivalent': "Master's or equivalent",
  'niveau doctorat ou equivalent': 'Doctorate or equivalent',
  "niveau d instruction inferieur au primaire": 'Below primary',
  'sans diplome': 'No qualification',
  // Marital status
  marie: 'Married',
  mariee: 'Married',
  'marie(e)': 'Married',
  celibataire: 'Single',
  divorce: 'Divorced',
  divorcee: 'Divorced',
  'divorce(e)': 'Divorced',
  veuf: 'Widowed',
  veuve: 'Widowed',
  'veuf/veuve': 'Widowed',
  'en couple': 'In a relationship',
  concubinage: 'In a relationship',
  'en concubinage': 'In a relationship',
  pacse: 'Civil partnership',
  pacsee: 'Civil partnership',
  'pacse(e)': 'Civil partnership',
  'en partenariat civil': 'Civil partnership',
  'dans une relation stable': 'In a relationship',
  // Education — variants seen live (incl. the "superérieur" double-é typo) + early childhood.
  'education de la petite enfance': 'Early childhood education',
  'enseignement supererieur de cycle court': 'Short-cycle tertiary',
  'enseignement supererieur de cycle long': 'Long-cycle tertiary',
  'enseignement supererieur universitaire': 'University education',
}

// Income categories appear as "Catégorie de revenus N" — sometimes with a trailing
// qualifier like "(la moins élevée)" — translate to "Income tier N" (QS-25).
const FRENCH_INCOME_RE = /^cat(?:e|é)gorie\s+de\s+revenus?\s*(\d+)\s*(?:\(.*\))?$/i

/** Normalize for lookup: lowercase, strip accents/diacritics, collapse whitespace. */
function normValue(v: string): string {
  return v
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Light tidy for an unknown attribute VALUE: trim + sentence-case the first letter,
 * leaving the rest intact (values are usually already human-readable). Used as the
 * fallback when no known-value translation applies.
 */
function humanizeValue(v: string): string {
  const s = v.trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : v
}

/**
 * Display label for a raw attribute VALUE: known-French → concise English via the
 * curated map (incl. "Catégorie de revenus N" → "Income tier N"); otherwise a light
 * tidy. Apply this wherever audience values are SHOWN (composition bars + segment
 * dropdown labels). Idempotent on already-English values.
 */
export function displayAttrValue(value: string): string {
  if (!value) return value
  const norm = normValue(value)
  const mapped = FRENCH_VALUE_MAP[norm]
  if (mapped) return mapped
  // QS-25 — compound values like "Marié(e) / en partenariat civil": map each side,
  // only collapsing to English when EVERY part is recognized (else fall through).
  if (norm.includes('/')) {
    const parts = norm.split('/').map((p) => p.trim()).filter(Boolean)
    const mappedParts = parts.map((p) => FRENCH_VALUE_MAP[p])
    if (parts.length > 1 && mappedParts.every(Boolean)) return mappedParts.join(' / ')
  }
  const inc = value.trim().match(FRENCH_INCOME_RE)
  if (inc) return `Income tier ${inc[1]}`
  // QS-25 — "Pas d'enfants (<18 ans)…" and its variants → concise English.
  if (/^pas d enfants?\b/.test(norm)) return 'No children under 18'
  return humanizeValue(value)
}

/**
 * Is a bare attribute-VALUE label AMBIGUOUS without its group/attribute name?
 * Format-driven only (never keyed on specific attributes): a value with no letters at
 * all ("1", "2-4", "10+") or a very short word ("High", "Low", "UK") says nothing on a
 * selected-cut chip or band header once the picker's group heading is out of sight.
 * Used to decide when a cut display label gets its group prefixed ("Tech comfort: 1");
 * longer self-descriptive values ("Weekly", "Married") stay bare.
 */
export function isAmbiguousSegmentValueLabel(label: string): boolean {
  const s = (label ?? '').trim()
  if (!s) return true
  if (!/[a-zA-Z]/.test(s)) return true
  return s.length <= 4
}

/**
 * The display label for a SELECTED cut chip / band header: the bare value label,
 * prefixed with its group/attribute label when the bare value alone is ambiguous
 * (see {@link isAmbiguousSegmentValueLabel}). Skips the prefix when there is no group
 * label or the value already contains it. Picker MENU options stay bare — the grouped
 * menu headings supply the context there.
 */
export function qualifiedCutDisplayLabel(groupLabel: string | undefined, valueLabel: string): string {
  const group = (groupLabel ?? '').trim()
  const value = (valueLabel ?? '').trim()
  if (!group || !value) return value
  if (!isAmbiguousSegmentValueLabel(value)) return value
  if (value.toLowerCase().includes(group.toLowerCase())) return value
  return `${group}: ${value}`
}

const SEG_PRIORITY = ['age', 'gender', 'sex', 'country', 'region', 'income', 'education']
export function segPriority(name: string): number {
  const i = SEG_PRIORITY.findIndex((p) => name.toLowerCase().includes(p))
  return i === -1 ? SEG_PRIORITY.length : i
}

/**
 * F1a — generic consumer-demographic classifier. True when an attribute KEY reads as a
 * consumer demographic (age / gender / income / household / …), driven off the key text
 * only — never a hardcoded audience or value. Used to suppress consumer-demographic
 * AUTO-cuts on a B2B audience's "Break down by" dropdown; a firmographic cut like
 * "company size", "seniority" or "industry" is NOT matched, so it survives.
 *
 * Intentionally INDEPENDENT of the Python lens (`_CONSUMER_DEMOGRAPHIC_RE`): we restate
 * the intent in TS rather than verbatim-port the regex, to avoid cross-language drift.
 * The key is humanized first (snake/camel → spaced lowercase) so "age_range",
 * "phoneToWeb" and "household_income" all classify on whole-word boundaries.
 */
const CONSUMER_DEMOGRAPHIC_RE =
  /\b(age|gender|sex|income|earnings|salary|wage|marital|married|household|children|kids|dependents|phone\s*to\s*web|device|generation|ethnicity|race|nationality)\b/i
export function isConsumerDemographicAttr(name: string): boolean {
  if (!name) return false
  return CONSUMER_DEMOGRAPHIC_RE.test(humanizeAttr(name).toLowerCase())
}

export interface CompositionColumn {
  name: string
  title: string
  total: number
  segments: Array<{ value: string; count: number; pct: number }>
}

/**
 * Turn a persona sample into a tidy demographic breakdown for the composition pane:
 * the top ~5 clean attributes (age/gender/country first), each with value shares.
 * Drops single-value and high-cardinality (>8) columns so the panel stays readable.
 */
export function buildComposition(
  personas: Array<{ attributes?: Record<string, unknown>; resolvedIdentity?: Record<string, unknown> }>,
): CompositionColumn[] {
  if (!personas.length) return []
  const tally = new Map<string, Map<string, number>>()
  for (const p of personas) {
    const attrs = { ...(p.attributes ?? {}), ...(p.resolvedIdentity ?? {}) }
    for (const [key, raw] of Object.entries(attrs)) {
      if (!isCleanSegmentKey(key)) continue
      if (raw == null || typeof raw === 'object') continue
      const val = String(raw).trim()
      if (!val) continue
      if (!tally.has(key)) tally.set(key, new Map())
      const counts = tally.get(key)!
      counts.set(val, (counts.get(val) ?? 0) + 1)
    }
  }
  const cols: CompositionColumn[] = []
  for (const [name, counts] of tally) {
    const distinct = [...counts.keys()]
    if (distinct.length < 2 || distinct.length > 8) continue
    const total = [...counts.values()].reduce((a, b) => a + b, 0)
    const segments = distinct
      .map((value) => {
        const count = counts.get(value)!
        return { value, count, pct: Math.round((count / total) * 100) }
      })
      .sort((a, b) => b.count - a.count)
    cols.push({ name, title: humanizeAttr(name), total, segments })
  }
  return cols.sort((a, b) => segPriority(a.name) - segPriority(b.name)).slice(0, 5)
}

/* -------------------------------------------------------------------------- */
/*  Fork-aware audience helpers (Audience pane top section rework)             */
/* -------------------------------------------------------------------------- */

/**
 * Segmentation categories relevant to a B2B audience: the firm/role attributes plus
 * shared geography. Consumer demographics (age/gender/income…) are intentionally
 * excluded so a B2B breakdown stays on-topic.
 */
export const B2B_CATEGORIES: ReadonlySet<SegmentationCategory> = new Set<SegmentationCategory>([
  'firmographics',
  'professional',
  'geography',
])

/**
 * Segmentation categories relevant to a B2C audience: consumer demographics plus
 * shared geography. Firmographic/professional attributes are excluded.
 */
export const B2C_CATEGORIES: ReadonlySet<SegmentationCategory> = new Set<SegmentationCategory>([
  'demographics',
  'geography',
])

/**
 * Map an audience-type string to its allowed segmentation categories. Case-insensitive
 * on 'B2B'/'B2C'; anything else (including null/undefined) returns null so callers can
 * treat "unknown type" as "no fork, fall back to the generic composition".
 */
export function categoriesForAudienceType(
  t?: string | null,
): ReadonlySet<SegmentationCategory> | null {
  if (!t) return null
  const norm = t.trim().toLowerCase()
  if (norm === 'b2b') return B2B_CATEGORIES
  if (norm === 'b2c') return B2C_CATEGORIES
  return null
}

/**
 * One horizontal bar per chosen segment for the audience-size visual. `count` is read
 * from `sizes` keyed by tag id (missing → 0); `pct` is the count relative to the LARGEST
 * bar (max-count → 100, and 0 when every bar is empty) so the widest bar always fills.
 */
export interface SegmentBar {
  label: string
  count: number
  pct: number
}

/**
 * Build the segment-size bars. With chosen tags: one bar each (input order preserved),
 * counts from `sizes`, percentages scaled to the largest bar. With no chosen tags: [] —
 * the composition pane only shows this block when the session has subsegment cuts.
 */
export function buildSegmentBars(
  chosenTags: Array<AudienceTag>,
  sizes: Record<string, number>,
  _totalAvailable: number | null,
): SegmentBar[] {
  if (!chosenTags.length) {
    return []
  }
  const bars = chosenTags.map((tag) => ({
    label: tag.name,
    count: sizes[tag.id] ?? 0,
  }))
  const max = bars.reduce((m, b) => Math.max(m, b.count), 0)
  return bars.map((b) => ({
    ...b,
    pct: max > 0 ? Math.round((b.count / max) * 100) : 0,
  }))
}

/**
 * Fork-aware demographic/firmographic breakdown for the composition pane. Unlike
 * {@link buildComposition} (which infers columns from raw "clean" attribute keys), this
 * drives off explicit canonical mappings and the audience fork:
 *
 *  - `allowed` = {@link categoriesForAudienceType}. If it's null (UNKNOWN audience type —
 *    e.g. a legacy audience), fall back to {@link buildComposition} so we never regress.
 *  - If the fork IS active (known B2B/B2C) but there are no usable mapped fields for it,
 *    return [] (show nothing) rather than mislabel a B2B audience with consumer
 *    demographics — we never fall back to the generic breakdown once a fork applies.
 *  - Otherwise keep only mappings whose canonical category (derived via
 *    {@link getCategoryForCanonicalId}) is in `allowed` — e.g. a B2B audience drops a
 *    `std_dem_age` (demographics) column but keeps firmographics/professional/geography.
 *  - Each kept mapping's `sourceColumn` is tallied across the persona sample using the
 *    SAME value-share + cardinality rules as buildComposition (drop <2 or >8 distinct).
 *    The column `title` is the mapping's `displayLabel` (mapped columns bypass
 *    `isCleanSegmentKey`/`humanizeAttr`); values are still tidied via `displayAttrValue`.
 *  - Columns are ordered by their category's index in {@link CATEGORY_ORDER}, stable
 *    within a category (i.e. preserving the input mapping order).
 */
export function buildMappedComposition(
  personas: Array<{ attributes?: Record<string, unknown>; resolvedIdentity?: Record<string, unknown> }>,
  mappings: Array<{ sourceColumn: string; canonicalId: string | null; displayLabel: string }> | null | undefined,
  audienceType?: string | null,
): CompositionColumn[] {
  const allowed = categoriesForAudienceType(audienceType)

  // Keep only mappings whose derived category is allowed by the fork. A null category
  // (unknown/custom canonicalId) or a category outside `allowed` is skipped.
  const kept = allowed
    ? (mappings ?? []).filter((m) => {
        const category = getCategoryForCanonicalId(m.canonicalId)
        return category != null && allowed.has(category)
      })
    : []

  // Unknown audience type → no fork → generic composition (legacy, no regression).
  if (!allowed) return buildComposition(personas)

  // Fork active but nothing to break down by (no allowed mappings, or no sample to tally) →
  // show nothing rather than fall back to off-fork demographics.
  if (!kept.length || !personas.length) return []

  // Tally each kept mapping's source column across the merged persona attributes.
  const cols: Array<{ category: SegmentationCategory; order: number; col: CompositionColumn }> = []
  kept.forEach((m, order) => {
    const category = getCategoryForCanonicalId(m.canonicalId)!
    const counts = new Map<string, number>()
    for (const p of personas) {
      const attrs = { ...(p.attributes ?? {}), ...(p.resolvedIdentity ?? {}) }
      const raw = attrs[m.sourceColumn]
      if (raw == null || typeof raw === 'object') continue
      const val = String(raw).trim()
      if (!val) continue
      counts.set(val, (counts.get(val) ?? 0) + 1)
    }
    const distinct = [...counts.keys()]
    if (distinct.length < 2 || distinct.length > 8) return // cardinality filter
    const total = [...counts.values()].reduce((a, b) => a + b, 0)
    const segments = distinct
      .map((value) => {
        const count = counts.get(value)!
        return { value: displayAttrValue(value), count, pct: Math.round((count / total) * 100) }
      })
      .sort((a, b) => b.count - a.count)
    cols.push({
      category,
      order,
      col: { name: m.sourceColumn, title: m.displayLabel, total, segments },
    })
  })

  // Every kept mapping was filtered out by cardinality → show nothing (fork is active, so we
  // never fall back to the off-fork generic breakdown).
  if (!cols.length) return []

  // Order by category (CATEGORY_ORDER), stable within a category by input order.
  return cols
    .sort((a, b) => {
      const byCategory = CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category)
      return byCategory !== 0 ? byCategory : a.order - b.order
    })
    .map((c) => c.col)
}
